import React from 'react'
import { Alert, Button, Flex, Popconfirm, Tag } from 'antd'
import { useTranslation } from 'react-i18next'
import { ToolActivity, ToolTestMode } from '../types'
import { useAuth } from '../../auth'
import { STORE_APP_CONNECTION } from '../../user/permission'

type ToolTestControlsProps = {
    mode: ToolTestMode
    onChange: (mode: ToolTestMode) => void
    activities: ToolActivity[]
    disabled: boolean
}

const activityColors: Record<ToolActivity['status'], string> = {
    simulated: 'orange',
    live: 'green',
    failed: 'red',
}

export function ToolTestControls({ mode, onChange, activities, disabled }: ToolTestControlsProps) {
    const { t } = useTranslation()
    const { can } = useAuth()
    const canStartLiveTest = mode === 'safe' && can(STORE_APP_CONNECTION)

    const activityLabels: Record<ToolActivity['status'], string> = {
        simulated: t('Simulated'),
        live: t('Executed'),
        failed: t('Failed'),
    }

    return (
        <Flex
            vertical
            gap="small"
            className="mb-4"
        >
            <Alert
                type={mode === 'live' ? 'warning' : 'info'}
                message={
                    mode === 'live'
                        ? t('Live test: actions can make real changes to connected apps.')
                        : t('Safe test: reads use real data. Changes are simulated.')
                }
            />
            {canStartLiveTest && (
                <Popconfirm
                    title={t('Start a live test? Actions can send messages and change real data.')}
                    description={t('Changing test mode starts a new conversation.')}
                    onConfirm={() => onChange('live')}
                    disabled={disabled}
                >
                    <Button disabled={disabled}>{t('Use live test')}</Button>
                </Popconfirm>
            )}
            {mode === 'live' && (
                <Button
                    disabled={disabled}
                    onClick={() => onChange('safe')}
                >
                    {t('Use safe test')}
                </Button>
            )}
            <Flex
                wrap
                gap="small"
            >
                {activities.map((activity, index) => (
                    <Tag
                        key={index}
                        color={activityColors[activity.status]}
                    >
                        {activity.name}: {activityLabels[activity.status]}
                    </Tag>
                ))}
            </Flex>
        </Flex>
    )
}
